import { MIN_SPACE_DIM_FEET, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  FootprintShapeType,
  PositionAndDimensionsType,
  PositionType
} from 'data/types';
import type { ResizeDirection } from './grid';

type PolygonPoint = { x: number; y: number };

export function isPointInPolygon(
  point: PolygonPoint,
  polygon: PolygonPoint[]
): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }
  return inside;
}

export function isPointOnPolygonEdge(
  point: PolygonPoint,
  polygon: PolygonPoint[],
  tolerance: number = 0.001
): boolean {
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const x1 = polygon[j].x;
    const y1 = polygon[j].y;
    const x2 = polygon[i].x;
    const y2 = polygon[i].y;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      if (
        Math.abs(point.x - x1) < tolerance &&
        Math.abs(point.y - y1) < tolerance
      )
        return true;
    } else {
      const t = ((point.x - x1) * dx + (point.y - y1) * dy) / lengthSq;
      if (t >= -tolerance && t <= 1 + tolerance) {
        const projX = x1 + t * dx;
        const projY = y1 + t * dy;
        const dist = Math.hypot(point.x - projX, point.y - projY);
        if (dist < tolerance) return true;
      }
    }
  }
  return false;
}

export function isPointInsideOrOnPolygon(
  point: PolygonPoint,
  polygon: PolygonPoint[]
): boolean {
  return (
    isPointInPolygon(point, polygon) ||
    isPointOnPolygonEdge(point, polygon, 0.01)
  );
}

export function isRectInPolygon(
  rect: PositionAndDimensionsType,
  polygon: PolygonPoint[]
): boolean {
  if (polygon.length < 3) return true;

  const corners: PolygonPoint[] = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height }
  ];

  return corners.every((corner) => isPointInsideOrOnPolygon(corner, polygon));
}

export function polygonBoundingBox(polygon: PolygonPoint[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

function findBestPositionInPolygon(
  width: number,
  height: number,
  polygon: PolygonPoint[],
  bb: { minX: number; minY: number; maxX: number; maxY: number },
  preferred: { x: number; y: number }
): { x: number; y: number } | null {
  let bestDist = Infinity;
  let bestPos: { x: number; y: number } | null = null;

  const step = 1;
  for (let testX = bb.minX; testX + width <= bb.maxX; testX += step) {
    for (let testY = bb.minY; testY + height <= bb.maxY; testY += step) {
      if (isRectInPolygon({ x: testX, y: testY, width, height }, polygon)) {
        const dist =
          Math.abs(testX - preferred.x) + Math.abs(testY - preferred.y);
        if (dist < bestDist) {
          bestDist = dist;
          bestPos = { x: testX, y: testY };
        }
      }
    }
  }

  return bestPos;
}

export function clampRectToFootprint(
  rect: PositionAndDimensionsType,
  polygon: PolygonPoint[]
): PositionAndDimensionsType {
  if (polygon.length < 3) return rect;
  if (isRectInPolygon(rect, polygon)) return rect;

  const bb = polygonBoundingBox(polygon);
  let { x, y, width, height } = rect;

  const maxWidth = bb.maxX - bb.minX;
  const maxHeight = bb.maxY - bb.minY;
  if (width > maxWidth) width = maxWidth;
  if (height > maxHeight) height = maxHeight;

  if (x < bb.minX) x = bb.minX;
  if (y < bb.minY) y = bb.minY;
  if (x + width > bb.maxX) x = bb.maxX - width;
  if (y + height > bb.maxY) y = bb.maxY - height;

  if (isRectInPolygon({ x, y, width, height }, polygon)) {
    return { x, y, width, height };
  }

  const bestPosition = findBestPositionInPolygon(width, height, polygon, bb, {
    x: rect.x,
    y: rect.y
  });
  if (bestPosition) {
    return { ...bestPosition, width, height };
  }

  const minDim = MIN_SPACE_DIM_FEET;
  for (
    let shrink = 1;
    shrink <= Math.max(width, height) - minDim;
    shrink += 1
  ) {
    const w = Math.max(minDim, width - shrink);
    const h = Math.max(minDim, height - shrink);
    const pos = findBestPositionInPolygon(w, h, polygon, bb, {
      x: rect.x,
      y: rect.y
    });
    if (pos) {
      return { x: pos.x, y: pos.y, width: w, height: h };
    }
  }

  return { x: bb.minX, y: bb.minY, width: minDim, height: minDim };
}

function isEdgeInsidePolygon(
  rect: PositionAndDimensionsType,
  edge: 'right' | 'bottom' | 'left' | 'top',
  polygon: PolygonPoint[]
): boolean {
  const { x, y, width, height } = rect;
  switch (edge) {
    case 'right':
      return (
        isPointInsideOrOnPolygon({ x: x + width, y }, polygon) &&
        isPointInsideOrOnPolygon({ x: x + width, y: y + height }, polygon)
      );
    case 'bottom':
      return (
        isPointInsideOrOnPolygon({ x, y: y + height }, polygon) &&
        isPointInsideOrOnPolygon({ x: x + width, y: y + height }, polygon)
      );
    case 'left':
      return (
        isPointInsideOrOnPolygon({ x, y }, polygon) &&
        isPointInsideOrOnPolygon({ x, y: y + height }, polygon)
      );
    case 'top':
      return (
        isPointInsideOrOnPolygon({ x, y }, polygon) &&
        isPointInsideOrOnPolygon({ x: x + width, y }, polygon)
      );
    default:
      return false;
  }
}

function snapEdgeToPolygonBoundary(
  result: PositionAndDimensionsType,
  edge: 'right' | 'bottom' | 'left' | 'top',
  polygon: PolygonPoint[]
): PositionAndDimensionsType {
  const snapped = { ...result };

  if (edge === 'right') {
    const candidateRight = Math.ceil(snapped.x + snapped.width);
    if (candidateRight > snapped.x + snapped.width) {
      const trial = { ...snapped, width: candidateRight - snapped.x };
      if (isEdgeInsidePolygon(trial, 'right', polygon)) {
        snapped.width = trial.width;
      }
    }
  } else if (edge === 'bottom') {
    const candidateBottom = Math.ceil(snapped.y + snapped.height);
    if (candidateBottom > snapped.y + snapped.height) {
      const trial = { ...snapped, height: candidateBottom - snapped.y };
      if (isEdgeInsidePolygon(trial, 'bottom', polygon)) {
        snapped.height = trial.height;
      }
    }
  } else if (edge === 'left') {
    const candidateLeft = Math.floor(snapped.x);
    if (candidateLeft < snapped.x) {
      const extra = snapped.x - candidateLeft;
      const trial = {
        ...snapped,
        x: candidateLeft,
        width: snapped.width + extra
      };
      if (isEdgeInsidePolygon(trial, 'left', polygon)) {
        snapped.x = trial.x;
        snapped.width = trial.width;
      }
    }
  } else if (edge === 'top') {
    const candidateTop = Math.floor(snapped.y);
    if (candidateTop < snapped.y) {
      const extra = snapped.y - candidateTop;
      const trial = {
        ...snapped,
        y: candidateTop,
        height: snapped.height + extra
      };
      if (isEdgeInsidePolygon(trial, 'top', polygon)) {
        snapped.y = trial.y;
        snapped.height = trial.height;
      }
    }
  }

  return snapped;
}

export function constrainResizeToFootprint(
  position: PositionAndDimensionsType,
  polygon: PolygonPoint[],
  direction: ResizeDirection
): PositionAndDimensionsType {
  if (polygon.length < 3) return position;

  const result = { ...position };

  const bb = polygonBoundingBox(polygon);
  const isFreeRight = ['right', 'ne', 'se'].includes(direction);
  const isFreeLeft = ['left', 'nw', 'sw'].includes(direction);
  const isFreeBottom = ['bottom', 'sw', 'se'].includes(direction);
  const isFreeTop = ['top', 'nw', 'ne'].includes(direction);

  if (isFreeRight && result.x + result.width > bb.maxX) {
    result.width = Math.max(MIN_SPACE_DIM_FEET, bb.maxX - result.x);
  }
  if (isFreeBottom && result.y + result.height > bb.maxY) {
    result.height = Math.max(MIN_SPACE_DIM_FEET, bb.maxY - result.y);
  }
  if (isFreeLeft && result.x < bb.minX) {
    const overshoot = bb.minX - result.x;
    result.x = bb.minX;
    result.width = Math.max(MIN_SPACE_DIM_FEET, result.width - overshoot);
  }
  if (isFreeTop && result.y < bb.minY) {
    const overshoot = bb.minY - result.y;
    result.y = bb.minY;
    result.height = Math.max(MIN_SPACE_DIM_FEET, result.height - overshoot);
  }

  if (['right', 'ne', 'se'].includes(direction)) {
    while (
      result.width > MIN_SPACE_DIM_FEET &&
      !isEdgeInsidePolygon(result, 'right', polygon)
    ) {
      result.width -= 1;
    }
    const refined = snapEdgeToPolygonBoundary(result, 'right', polygon);
    result.width = refined.width;
  }
  if (['bottom', 'se', 'sw'].includes(direction)) {
    while (
      result.height > MIN_SPACE_DIM_FEET &&
      !isEdgeInsidePolygon(result, 'bottom', polygon)
    ) {
      result.height -= 1;
    }
    const refined = snapEdgeToPolygonBoundary(result, 'bottom', polygon);
    result.height = refined.height;
  }
  if (['left', 'nw', 'sw'].includes(direction)) {
    while (
      result.width > MIN_SPACE_DIM_FEET &&
      !isEdgeInsidePolygon(result, 'left', polygon)
    ) {
      result.x += 1;
      result.width -= 1;
    }
    const refined = snapEdgeToPolygonBoundary(result, 'left', polygon);
    result.x = refined.x;
    result.width = refined.width;
  }
  if (['top', 'nw', 'ne'].includes(direction)) {
    while (
      result.height > MIN_SPACE_DIM_FEET &&
      !isEdgeInsidePolygon(result, 'top', polygon)
    ) {
      result.y += 1;
      result.height -= 1;
    }
    const refined = snapEdgeToPolygonBoundary(result, 'top', polygon);
    result.y = refined.y;
    result.height = refined.height;
  }

  const eps = 1e-9;
  for (const v of polygon) {
    const insideX = v.x > result.x + eps && v.x < result.x + result.width - eps;
    const insideY =
      v.y > result.y + eps && v.y < result.y + result.height - eps;
    if (insideX && insideY) {
      if (isFreeRight && v.x > result.x) {
        result.width = Math.max(
          MIN_SPACE_DIM_FEET,
          Math.min(result.width, v.x - result.x)
        );
      }
      if (isFreeLeft && v.x < result.x + result.width) {
        const right = result.x + result.width;
        result.x = Math.min(
          right - MIN_SPACE_DIM_FEET,
          Math.max(result.x, v.x)
        );
        result.width = Math.max(MIN_SPACE_DIM_FEET, right - result.x);
      }
      if (isFreeBottom && v.y > result.y) {
        result.height = Math.max(
          MIN_SPACE_DIM_FEET,
          Math.min(result.height, v.y - result.y)
        );
      }
      if (isFreeTop && v.y < result.y + result.height) {
        const bottom = result.y + result.height;
        result.y = Math.min(
          bottom - MIN_SPACE_DIM_FEET,
          Math.max(result.y, v.y)
        );
        result.height = Math.max(MIN_SPACE_DIM_FEET, bottom - result.y);
      }
    }
  }

  for (let i = 0; i < polygon.length; i += 1) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];

    for (const checkY of [result.y, result.y + result.height]) {
      if ((p1.y - checkY) * (p2.y - checkY) < 0) {
        const t = (checkY - p1.y) / (p2.y - p1.y);
        const xCross = p1.x + t * (p2.x - p1.x);
        if (xCross > result.x + eps && xCross < result.x + result.width - eps) {
          if (isFreeRight && xCross > result.x) {
            result.width = Math.max(
              MIN_SPACE_DIM_FEET,
              Math.min(result.width, xCross - result.x)
            );
          }
          if (isFreeLeft && xCross < result.x + result.width) {
            const right = result.x + result.width;
            result.x = Math.min(
              right - MIN_SPACE_DIM_FEET,
              Math.max(result.x, xCross)
            );
            result.width = Math.max(MIN_SPACE_DIM_FEET, right - result.x);
          }
        }
      }
    }

    for (const checkX of [result.x, result.x + result.width]) {
      if ((p1.x - checkX) * (p2.x - checkX) < 0) {
        const t = (checkX - p1.x) / (p2.x - p1.x);
        const yCross = p1.y + t * (p2.y - p1.y);
        if (
          yCross > result.y + eps &&
          yCross < result.y + result.height - eps
        ) {
          if (isFreeBottom && yCross > result.y) {
            result.height = Math.max(
              MIN_SPACE_DIM_FEET,
              Math.min(result.height, yCross - result.y)
            );
          }
          if (isFreeTop && yCross < result.y + result.height) {
            const bottom = result.y + result.height;
            result.y = Math.min(
              bottom - MIN_SPACE_DIM_FEET,
              Math.max(result.y, yCross)
            );
            result.height = Math.max(MIN_SPACE_DIM_FEET, bottom - result.y);
          }
        }
      }
    }
  }

  if (!isRectInPolygon(result, polygon)) {
    return clampRectToFootprint(position, polygon);
  }

  return result;
}

export function footprintToPixelPath(footprint: PositionType[]): string {
  if (footprint.length < 3) return '';
  return footprint
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${p.x * STRUCTURE_GRID_SIZE} ${
          p.y * STRUCTURE_GRID_SIZE
        }`
    )
    .join(' ')
    .concat(' Z');
}

export function footprintPolygonsToPixelPath(
  polygons: PositionType[][]
): string {
  return polygons
    .map(footprintToPixelPath)
    .filter((path) => path.length > 0)
    .join(' ');
}

export function snapToGrid(value: number): number {
  return Math.round(value);
}

export function snapPointToGrid(point: PolygonPoint): PolygonPoint {
  return {
    x: snapToGrid(point.x),
    y: snapToGrid(point.y)
  };
}

export function snapTo90Degrees(
  rawPoint: PolygonPoint,
  anchor: PolygonPoint
): PolygonPoint {
  const dx = Math.abs(rawPoint.x - anchor.x);
  const dy = Math.abs(rawPoint.y - anchor.y);
  if (dx >= dy) {
    return { x: rawPoint.x, y: anchor.y };
  }
  return { x: anchor.x, y: rawPoint.y };
}

export function areFootprintsEqual(
  a: PositionType[] | null,
  b: PositionType[] | null
): boolean {
  const aEmpty = !a || a.length === 0;
  const bEmpty = !b || b.length === 0;
  if (aEmpty && bEmpty) return true;
  if (aEmpty || bEmpty) return false;
  if (a!.length !== b!.length) return false;
  return a!.every((p, i) => p.x === b![i].x && p.y === b![i].y);
}

export function areFootprintShapesEqual(
  a: FootprintShapeType[] | null | undefined,
  b: FootprintShapeType[] | null | undefined
): boolean {
  const aShapes = (a ?? []).filter((shape) => shape.points.length >= 3);
  const bShapes = (b ?? []).filter((shape) => shape.points.length >= 3);
  if (aShapes.length !== bShapes.length) return false;
  return aShapes.every((shape, index) =>
    areFootprintsEqual(shape.points, bShapes[index].points)
  );
}
